import type { Offer } from "@/models/simulation/subtypes";
import type { SimulationDemand } from "@/models/Demand";
import type { CalculatorOfferData } from "@/types/calculator";
import { useCalculatorConsts } from "@/composables/calculator/useCalculatorConsts";
import { useCalculatorInsurances } from "@/composables/calculator/useCalculatorInsurances";
import { useCalculatorOfferCustomization } from "@/composables/calculator/table/useCalculatorOfferCustomization";
import { useCalculatorOfferInstallmentSegments } from "@/composables/calculator/table/useCalculatorOfferInstallmentSegments";

/**
 * The headline value of each offers-table row without a composable of its own, in one place.
 * The table, the printout and the offer sorting all read it from here, so sorting by a row
 * orders the offers by the very number that row shows.
 *
 * `offerData` is what the caller already holds - the table caches it per offer, the printout
 * builds it per row - and passing it in keeps these getters off a second, identical computation.
 */
export const useSimulationOfferRowValues = () => {
  const { FROM_PERIOD } = useCalculatorConsts();
  const { getInsuranceIndexForMonth } = useCalculatorInsurances();
  const { createOfferInstallmentSegments } = useCalculatorOfferInstallmentSegments();
  const { createOfferCustomization } = useCalculatorOfferCustomization();

  /** `installments` row: the net installment of the first segment - insurances excluded. */
  const getInstallmentAmount = (
    offer: Offer, userInput: Nullable<SimulationDemand>, offerData?: Partial<CalculatorOfferData>,
  ): Nullable<number> =>
    (offerData?.installmentSegments ?? createOfferInstallmentSegments(offer, userInput))[0]?.startAmount ?? null;

  /** `monthly-costs` row: the first month - its installment plus every insurance whose period already covers that month. */
  const getMonthlyCostsAmount = (
    offer: Offer, userInput: Nullable<SimulationDemand>, offerData?: Partial<CalculatorOfferData>,
  ): number => {
    const installment: number = getInstallmentAmount(offer, userInput, offerData) ?? 0;

    const realEstateInsurances = offer.monthlyFees.realEstateInsurance ?? [];
    const lifeInsurances = offer.monthlyFees.lifeInsurance ?? [];

    // nothing billed in the first month means index -1, which reads as undefined - hence the fallbacks to 0
    const realEstateFee: number = realEstateInsurances[getInsuranceIndexForMonth(realEstateInsurances, FROM_PERIOD)]?.fee ?? 0;
    const lifeFee: number = lifeInsurances[getInsuranceIndexForMonth(lifeInsurances, FROM_PERIOD)]?.fee ?? 0;

    return installment + realEstateFee + lifeFee;
  };

  /** `initial-costs` row: everything paid up front - a credited position drops out, it is repaid within the loan instead. */
  const getInitialCostsSum = (offer: Offer): number => {
    const provision: number = offer.creditedCosts.includes('Provision') ? 0 : offer.initialCosts.provision.cost;
    const realEstateInsurance: number = offer.creditedCosts.includes('RealEstateInsurance') ? 0 : offer.initialCosts.realEstateInsurance?.fee ?? 0;
    const lifeInsurance: number = offer.creditedCosts.includes('LifeInsurance') ? 0 : offer.initialCosts.lifeInsurance?.fee ?? 0;
    const paymentInsurance: number = offer.creditedCosts.includes('PaymentInsurance') ? 0 : offer.initialCosts.paymentInsurance?.fee ?? 0;
    const guaranteeProvision: number = offer.initialCosts.guaranteeProvision?.cost ?? 0;
    const realEstateAppraisal: number = offer.initialCosts.realEstateAppraisal?.sum ?? 0;

    return provision + realEstateInsurance + lifeInsurance + paymentInsurance + guaranteeProvision + realEstateAppraisal;
  };

  /** `interest-rate` row: the nominal rate of the first segment. */
  const getInterestRatePercentage = (
    offer: Offer, userInput: Nullable<SimulationDemand>, offerData?: Partial<CalculatorOfferData>,
  ): Nullable<number> =>
    (offerData?.installmentSegments ?? createOfferInstallmentSegments(offer, userInput))[0]?.percentage ?? null;

  /** `interest-rate-margin` leaf: the margin actually applied - what the cell's `default+diff=current` equation resolves to. */
  const getMargin = (
    offer: Offer, userInput: Nullable<SimulationDemand>, offerData?: Partial<CalculatorOfferData>,
  ): number =>
    (offerData?.customization ?? createOfferCustomization(offer, userInput)).margin.current;

  return {
    getInstallmentAmount,
    getMonthlyCostsAmount,
    getInitialCostsSum,
    getInterestRatePercentage,
    getMargin,
  };
};
